import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import CaptchaField from 'components/ui/CaptchaField';
import * as usersApi from 'shared/api/auth/usersApi';
import { Form, Input, Alert, Progress, Flex } from 'antd';

const STRENGTH_COLOR = {
  weak: '#ff4d4f',
  medium: '#faad14',
  good: '#1677ff',
  strong: '#52c41a'
};

const AccountDetailsStep = ({ formData, updateFormData, captchaRef }) => {
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [errors, setErrors] = useState({});
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null });

  // Debounced username availability check
  useEffect(() => {
    if (!formData.username || formData.username.length < 3) {
      setUsernameStatus({ checking: false, available: null });
      return;
    }

    const timer = setTimeout(async () => {
      setUsernameStatus({ checking: true, available: null });
      try {
        const result = await usersApi.checkUsernameAvailability(formData.username);
        setUsernameStatus({ checking: false, available: result.isAvailable });
      } catch (err) {
        setUsernameStatus({ checking: false, available: null });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username]);

  const passwordRequirements = [
    { id: 'length', text: 'Минимум 8 символов', check: (pwd) => pwd.length >= 8 },
    { id: 'uppercase', text: 'Одна заглавная буква', check: (pwd) => /[A-Z]/.test(pwd) },
    { id: 'lowercase', text: 'Одна строчная буква', check: (pwd) => /[a-z]/.test(pwd) },
    { id: 'number', text: 'Одна цифра', check: (pwd) => /\d/.test(pwd) },
    { id: 'special', text: 'Один специальный символ', check: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd) }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getPasswordStrength = (password) => {
    const passedRequirements = passwordRequirements.filter(req => req.check(password)).length;
    if (passedRequirements <= 2) return { level: 'weak', text: 'Слабый' };
    if (passedRequirements <= 3) return { level: 'medium', text: 'Средний' };
    if (passedRequirements <= 4) return { level: 'good', text: 'Хороший' };
    return { level: 'strong', text: 'Сильный' };
  };

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
    
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Real-time validation
    if (field === 'email' && value && !validateEmail(value)) {
      setErrors(prev => ({ ...prev, email: 'Введите корректный email адрес' }));
    }

    if (field === 'confirmPassword' || (field === 'password' && formData.confirmPassword)) {
      const password = field === 'password' ? value : formData.password;
      const confirmPassword = field === 'confirmPassword' ? value : formData.confirmPassword;
      
      if (confirmPassword && password !== confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Пароли не совпадают' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passedCount = passwordRequirements.filter(req => req.check(formData.password)).length;

  return (
    <Form layout="vertical" className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Создание аккаунта
        </h3>
        <p className="text-text-secondary">
          Введите данные для входа в систему
        </p>
      </div>

      {/* Email Field */}
      <Form.Item
        label="Email адрес *"
        validateStatus={errors.email ? 'error' : undefined}
        help={errors.email || 'Используйте корпоративный email адрес'}
      >
        <Input
          size="large"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          placeholder="example@company.com"
          suffix={<Icon name="Mail" size={16} className={errors.email ? 'text-error' : 'text-secondary-400'} />}
        />
      </Form.Item>

      {/* Username Field */}
      <Form.Item
        label="Имя пользователя *"
        validateStatus={
          usernameStatus.available === false ? 'error' : usernameStatus.available === true ? 'success' : undefined
        }
        help={
          usernameStatus.available === false
            ? 'Это имя пользователя уже занято'
            : usernameStatus.available === true
              ? 'Имя пользователя доступно' : 'Минимум 3 символа, латиница и цифры'
        }
      >
        <Input
          size="large"
          value={formData.username || ''}
          onChange={(e) => handleInputChange('username', e.target.value)}
          placeholder="username"
          minLength={3}
          suffix={
            usernameStatus.checking ? (
              <Icon name="Loader2" size={16} className="text-secondary-400 animate-spin" />
            ) : usernameStatus.available === true ? (
              <Icon name="Check" size={16} className="text-success" />
            ) : usernameStatus.available === false ? (
              <Icon name="X" size={16} className="text-error" />
            ) : (
              <Icon name="User" size={16} className="text-secondary-400" />
            )
          }
        />
      </Form.Item>

      {/* Password Field */}
      <Form.Item label="Пароль *" className="mb-2">
        <Input.Password
          size="large"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          onFocus={() => setShowPasswordRequirements(true)}
          placeholder="Введите надежный пароль"
        />
      </Form.Item>

      {/* Password Strength Indicator */}
      {formData.password && (
        <Flex align="center" gap={8} className="-mt-4 mb-2">
          <Progress
            percent={(passedCount / passwordRequirements.length) * 100}
            showInfo={false}
            size="small"
            strokeColor={STRENGTH_COLOR[passwordStrength.level]}
            className="flex-1"
          />
          <span className="text-xs font-medium text-text-secondary">
            {passwordStrength.text}
          </span>
        </Flex>
      )}

      {/* Password Requirements */}
      {showPasswordRequirements && (
        <Alert
          type="info"
          className="mb-2"
          message="Требования к паролю:"
          description={
            <div className="space-y-1 mt-1">
              {passwordRequirements.map(req => (
                <div key={req.id} className="flex items-center gap-2">
                  <Icon
                    name={req.check(formData.password) ? 'Check' : 'X'}
                    size={12}
                    className={req.check(formData.password) ? 'text-success' : 'text-secondary-400'}
                  />
                  <span className={`text-xs ${req.check(formData.password) ? 'text-success' : 'text-text-secondary'}`}>
                    {req.text}
                  </span>
                </div>
              ))}
            </div>
          }
        />
      )}

      {/* Confirm Password Field */}
      <Form.Item
        label="Подтверждение пароля *"
        validateStatus={
          errors.confirmPassword
            ? 'error'
            : (formData.confirmPassword && formData.password === formData.confirmPassword ? 'success' : undefined)
        }
        help={
          errors.confirmPassword
          || (formData.confirmPassword && formData.password === formData.confirmPassword ? 'Пароли совпадают' : undefined)
        }
      >
        <Input.Password
          size="large"
          value={formData.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
          placeholder="Повторите пароль"
        />
      </Form.Item>

      {/* Terms and Privacy */}
      <Alert
        type="info"
        showIcon
        message={
          <span className="text-text-secondary">
            Создавая аккаунт, вы соглашаетесь с{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-700 underline">
              Условиями использования
            </a>{' '}
            и{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-700 underline">
              Политикой конфиденциальности
            </a>
          </span>
        }
      />

      {/* Captcha */}
      {captchaRef && (
        <div className="pt-4 border-t border-secondary-200">
          <CaptchaField ref={captchaRef} />
        </div>
      )}
    </Form>
  );
};

export default AccountDetailsStep;